import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      // Verify token and get user info
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setUser(res.data);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        })
        .catch(() => {
          // Token invalid, clear it
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common["Authorization"];
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username, pin) => {
    const res = await axios.post(`${API}/auth/login`, { username, pin });
    const { token: newToken, user: userData } = res.data;
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  const isAdmin = user?.role === "admin";
  
  // Permission check helpers
  const hasPageAccess = (page) => {
    if (!user) return false;
    if (isAdmin) return true;
    return user.permissions?.pages?.includes(page) || false;
  };
  
  const hasFeatureAccess = (feature) => {
    if (!user) return false;
    if (isAdmin) return true;
    return user.permissions?.features?.includes(feature) || false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      token,
      hasPageAccess,
      hasFeatureAccess
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
